import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Campaigns.css";
import {
  FiEye,
  FiTarget,
  FiLock,
  FiTrash2,
  FiFeather,
  FiSearch
} from "react-icons/fi";
import CreateCampaignModal from "./CreateCampaignModal";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle";

const API = process.env.REACT_APP_API_URL;

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [acting, setActing] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignSearchLoading, setCampaignSearchLoading] = useState(false);
  const [campaignSearchResults, setCampaignSearchResults] = useState([]);
  const [, setCampaignSearchDone] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/campaigns`);
      setCampaigns(res.data || []);
    } catch (err) {
      console.error("Error cargando campañas", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const navigate = useNavigate();

  // ================= PAGINACIÓN =================
  const [page, setPage] = useState(1);
  const limit = 20;

  // PAGINACIÓN
  const total = campaigns.length;
  const pages = Math.ceil(total / limit);

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;

  const paginatedCampaigns = campaigns.slice(startIndex, endIndex);

  const closeCampaign = async (campaignId) => {
    await axios.patch(`${API}/api/campaigns/${campaignId}/close`);
  };

  const deleteCampaign = async (campaignId) => {
    await axios.delete(`${API}/api/campaigns/${campaignId}`);
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      setActing(true);

      const { type, campaign } = confirmAction;

      if (type === "close") {
        await closeCampaign(campaign.campaignId);
      }

      if (type === "delete") {
        await deleteCampaign(campaign.campaignId);
      }

      setConfirmAction(null);
      await loadCampaigns();
    } catch (err) {
      console.error("Error ejecutando acción", err);
    } finally {
      setActing(false);
    }
  };

  // Validar estado de firma
  const handleValidateSignatures = async () => {
    try {
      setValidating(true);
      setValidationResult(null);

      const res = await axios.post(
        `${API}/api/campaigns/${selectedCampaign?.campaignId}/validate-signatures`
      );

      setValidationResult(res.data);
    } catch (err) {
      console.error("Error validando firmas", err);
      setValidationResult({ error: true });
    } finally {
      setValidating(false);
    }
  };

  const handleSearchPersonInCampaigns = async () => {

    try {

      const value =
        campaignSearch.trim();

      if (!value) {
        return;
      }

      setCampaignSearchLoading(true);
      setCampaignSearchDone(false);

      const res = await axios.get(
        `${API}/api/campaigns/search/person`,
        {
          params: {
            q: value
          }
        }
      );

      setCampaignSearchResults(
        res.data?.data || []
      );

      setCampaignSearchDone(true);

      setShowSearchModal(true);

    } catch (err) {

      console.error(err);

      setCampaignSearchResults([]);
      setCampaignSearchDone(true);

      setShowSearchModal(true);

    } finally {

      setCampaignSearchLoading(false);

    }

  };

  const getInvitationStatusLabel = (
    status
  ) => {

    switch (status) {

      case "created":
        return "Creada";

      case "sent_to_sign":
        return "En firma";

      case "signed":
        return "Firmada";

      case "rejected":
        return "Rechazada";

      case "expired":
        return "Expirada";

      default:
        return status || "-";

    }

  };

  const getSignatureStatusLabel = (
    status
  ) => {

    switch (status) {

      case "pending":
        return "Pendiente";

      case "sent_to_sign":
        return "En firma";

      case "signed":
        return "Firmada";

      case "rejected":
        return "Rechazada";

      case "expired":
        return "Expirada";

      default:
        return status || "-";

    }

  };

  return (

    <>
    
    <PageTitle title="Campañas" />

    <div className="inv-root">
      {/* ================= HEADER ================= */}
      <header className="inv-header">
        <h2 className="inv-title">| Campañas</h2>

        <div className="inv-actions">
          <button className="inv-btn-primary"
            onClick={() => setShowCreate(true)}>
            <FiTarget size={15} /> Nueva campaña
          </button>
        </div>
      </header>

      <div className="inv-filters">
        <div className="inv-row">

          <div className="afil-field">

            <FiSearch />

            <input
              placeholder="Buscar persona por documento, nombre o celular..."
              value={campaignSearch}
              onChange={(e) =>
                setCampaignSearch(
                  e.target.value
                )
              }
              onKeyDown={(e) => {

                if (e.key === "Enter") {
                  handleSearchPersonInCampaigns();
                }

              }}
            />

          </div>

          <button
            className="inv-btn-primary"
            onClick={
              handleSearchPersonInCampaigns
            }
            disabled={
              campaignSearchLoading
            }
          >
            {
              campaignSearchLoading
                ? "Buscando..."
                : "Buscar"
            }
          </button>

        </div>
      </div>

      {/* TABLE */}
      <div className="inv-table">
        <table>
          <thead>
            <tr>
              <th>ID de campaña</th>
              <th>Nombre</th>
              <th>Fecha creación</th>
              <th>Fecha expiración</th>
              <th>Invitados</th>
              <th>Firmaron</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {campaigns.length === 0 && (
              <tr>
                <td colSpan="8" className="cam-empty">
                  No hay campañas creadas
                </td>
              </tr>
            )}

            {paginatedCampaigns.map((c) => (
              <tr key={c.campaignId}>
                <td>{c.campaignId}</td>
                <td>{c.name}</td>
                <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                <td>
                  {c.expiresAt
                    ? new Date(c.expiresAt).toLocaleDateString()
                    : "-"}
                </td>
                <td>{Number(c.stats?.total || 0).toLocaleString("es-CO")}</td>
                <td>{Number(c.stats?.signed || 0).toLocaleString("es-CO")}</td>
                <td>
                  <span className={`cam-status cam-${c.status}`}>
                    {c.status === "active" ? "Activa" : "Cerrada"}
                  </span>
                </td>
                <td className="inv-actions-row">
                  <button
                    className="inv-icon-btn"
                    onClick={() => navigate(`/campaigns/${c.campaignId}`)}
                    title="Ver detalle"
                  >
                    <FiEye />
                  </button>

                  <button
                    className="inv-icon-btn"

                    onClick={() => {
                      setSelectedCampaign(c);
                      setValidationResult(null);
                      setShowValidateModal(true);
                    }}

                    title="Validar firmas"
                  >
                    <FiFeather />
                  </button>

                  {/* Cerrar campaña (solo si está activa) */}
                  {c.status === "active" && (
                    <button
                      className="inv-icon-btn"
                      disabled={c.status !== "active"}
                      onClick={() => {
                        if (c.status === "active") {
                          setConfirmAction({ type: "close", campaign: c });
                        }
                      }}
                      title={c.status === "active" ? "Cerrar campaña" : "Campaña cerrada"}
                      style={{
                        opacity: c.status === "active" ? 1 : 0.4,
                        cursor: c.status === "active" ? "pointer" : "not-allowed"
                      }}
                    >
                      <FiLock />
                    </button>
                  )}

                  {/* Eliminar campaña */}
                  <button
                    className="inv-icon-btn"
                    onClick={() => setConfirmAction({ type: "delete", campaign: c })}
                    title="Eliminar campaña"
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ================= PAGINACIÓN ================= */}
        <div className="inv-paginacion">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Anterior
          </button>

          <span className="inv-pagina-info">
            Página {page} de {pages}
          </span>

          <button
            disabled={page === pages}
            onClick={() => setPage(page + 1)}
          >
            Siguiente
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateCampaignModal
          onClose={() => setShowCreate(false)}
          onCreated={loadCampaigns}
        />
      )}

      {confirmAction && (
        <div className="camp-modal-overlay">
          <div className="cam-detail-modal">

            <h3 style={{ textAlign: "center", marginBottom: 10 }}>
              {confirmAction.type === "close" ? "Cerrar campaña" : "Eliminar campaña"}
            </h3>

            <p style={{ textAlign: "center" }}>
              {confirmAction.type === "close" && (
                <>
                  ¿Deseas cerrar la campaña <b>{confirmAction.campaign.name}</b>?
                  <br />
                  No podrás enviar más invitaciones desde esta campaña.
                </>
              )}

              {confirmAction.type === "delete" && (
                <>
                  ¿Deseas eliminar la campaña <b>{confirmAction.campaign.name}</b>?
                  <br />
                  Esta acción eliminará también su base de invitados.
                </>
              )}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 14,
                marginTop: 22
              }}
            >
              <button
                className="cam-detail-btn-secondary"
                onClick={() => setConfirmAction(null)}
                disabled={acting}
              >
                Cancelar
              </button>

              <button
                className="cam-detail-btn-primary"
                onClick={executeConfirmAction}
                disabled={acting}
              >
                {acting ? "Procesando..." : (confirmAction.type === "close" ? "Cerrar" : "Eliminar")}
              </button>
            </div>

          </div>
        </div>
      )}

      {showValidateModal && (
        <div className="camp-modal-overlay">
          <div className="cam-detail-modal">
            <h3 style={{ textAlign: "center", marginBottom: 14 }}>
              Validación de Firmas
            </h3>

            {!validationResult && (
              <p style={{ textAlign: "center" }}>
                ¿Deseas validar las firmas de esta campaña?
              </p>
            )}

            {validationResult && !validationResult.error && (
              <div style={{ textAlign: "center", marginTop: 10 }}>
                <p><strong>Validación completada</strong></p>
                <p>Revisadas: {validationResult.checked}</p>
                <p>Firmadas nuevas: {validationResult.signed}</p>
                <p>Omitidas: {validationResult.skipped}</p>
              </div>
            )}

            {validationResult?.error && (
              <p style={{ color: "red", textAlign: "center" }}>
                Error validando firmas
              </p>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                marginTop: 22
              }}
            >
              <button
                className="cam-detail-btn-secondary"
                onClick={() => {
                  setShowValidateModal(false);
                  setValidationResult(null);
                  loadCampaigns();
                }}
                disabled={validating}
              >
                Cerrar
              </button>

              {!validationResult && (
                <button
                  className="cam-detail-btn-primary"
                  onClick={handleValidateSignatures}
                  disabled={validating}
                >
                  {validating ? "Validando..." : "Confirmar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showSearchModal && (

        <div className="camp-modal-overlay">

          <div
            className="cam-search-modal"
          >

            <div className="cam-search-modal-header">

              <h3 className="cam-search-modal-title">
                Resultado de Búsqueda
              </h3>

              <button
                className="cam-search-close"
                onClick={() => {

                  setShowSearchModal(false);

                }}
              >
                x
              </button>

            </div>

            {!campaignSearchResults.length ? (

              <div
                className="cam-search-empty"
              >

                No se encontró esta persona
                en ninguna campaña.

              </div>

            ) : (

              <div
                className="cam-search-table"
              >

                <table>

                  <thead>

                    <tr>
                      <th>ID</th>
                      <th>Campaña</th>
                      <th>Documento</th>
                      <th>Nombre</th>
                      <th>Celular</th>
                      <th>Estado</th>
                      <th>Firma</th>
                      <th></th>
                    </tr>

                  </thead>

                  <tbody>

                    {campaignSearchResults.map(
                      (item) => (

                        <tr
                          key={
                            item.invitationId
                          }
                        >

                          <td>
                            {item.campaignId}
                          </td>

                          <td>
                            {item.campaignName}
                          </td>

                          <td>
                            {
                              item.numeroDocumento
                            }
                          </td>

                          <td>
                            {item.nombres}
                          </td>

                          <td>
                            {item.celular}
                          </td>

                          <td>
                            {getInvitationStatusLabel(
                              item.status
                            )}
                          </td>

                          <td>
                            {getSignatureStatusLabel(
                              item.signatureStatus
                            )}
                          </td>

                          <td>

                            <button
                              className="inv-icon-btn"
                              onClick={() => {

                                setShowSearchModal(
                                  true
                                );

                                window.open(
                                  `/campaigns/${item.campaignId}`, "_blank"
                                );

                              }}
                            >
                              <FiEye />
                            </button>

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>

            )}

          </div>

        </div>

      )}

    </div>

    </>

  );

}

export default Campaigns;
